import type { QueryClient } from '@tanstack/react-query'
import { Link, Outlet, createRootRouteWithContext } from '@tanstack/react-router'

import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass, stateCopy } from '@/components/ui/empty-state-variants'

/**
 * What every route's `beforeLoad` and `loader` receive as `context`, supplied
 * once by `createRouter` (app/router.tsx).
 *
 * The QueryClient rather than an import of the app singleton, for the reason
 * `api/endpointCache.ts` gives for its own optional client argument: a test
 * mounts a route with its own fresh client, and a loader that reached past
 * the context to the module singleton would fill a cache no test owns.
 */
export interface RouterContext {
  queryClient: QueryClient
}

function PageNotFound() {
  return (
    <main className="flex min-h-dvh items-center justify-center px-4 py-10">
      <EmptyState
        kind="not-found"
        size="page"
        headingLevel="h1"
        title="Page not found"
        description={stateCopy('not-found').description}
        action={
          <Link to="/" className={stateActionClass('page')}>
            Go home
          </Link>
        }
      />
    </main>
  )
}

/**
 * Bare outlet. The app chrome lives in the `app` layout below, because the
 * onboarding and auth screens are full-bleed — they own the whole viewport in
 * Figma, header and sidebar included. Rendering AppShell here would have meant
 * every new full-bleed screen needing a way to opt out of it.
 *
 * No `errorComponent` of its own: the router's default (RouteErrorPage, set
 * in app/router.tsx) covers this route and every route below it that does
 * not bring one, and it already draws the whole-viewport state a failure out
 * here needs.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: PageNotFound,
})
